import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaGoogle } from "react-icons/fa";
import { FiUser, FiLock, FiEye, FiEyeOff, FiX } from "react-icons/fi";
import styles from "./LoginDark.module.css";
import { useAuth } from "../contexts/AuthContext";
import authService from "../services/authService";
import InlineSpinner from "../components/common/InlineSpinner";

const GoogleIcon = FaGoogle as React.FC<React.SVGProps<SVGSVGElement>>;
const UserIcon = FiUser as React.FC<React.SVGProps<SVGSVGElement>>;
const LockIcon = FiLock as React.FC<React.SVGProps<SVGSVGElement>>;
const EyeIcon = FiEye as React.FC<React.SVGProps<SVGSVGElement>>;
const EyeOffIcon = FiEyeOff as React.FC<React.SVGProps<SVGSVGElement>>;
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;

/**
 * Convert technical error messages to user-friendly messages
 */
const getUserFriendlyError = (error: string | null): string | null => {
  if (!error) return null;

  const errorLower = error.toLowerCase();

  // Invalid credentials
  if (
    errorLower.includes("invalid") ||
    errorLower.includes("credentials") ||
    errorLower.includes("email") ||
    errorLower.includes("password")
  ) {
    return "Invalid email or password. Please check your credentials and try again.";
  }

  // User not found
  if (errorLower.includes("user not found") || errorLower.includes("no user")) {
    return "No account found with this email. Please sign up first.";
  }

  // Email already exists (shouldn't happen on login, but handle it)
  if (
    errorLower.includes("already registered") ||
    errorLower.includes("already exists")
  ) {
    return "An account with this email already exists. Please sign in instead.";
  }

  // Network errors
  if (
    errorLower.includes("network") ||
    errorLower.includes("fetch") ||
    errorLower.includes("connection")
  ) {
    return "Network error. Please check your internet connection and try again.";
  }

  // Rate limiting
  if (errorLower.includes("rate limit") || errorLower.includes("too many")) {
    return "Too many login attempts. Please wait a few minutes and try again.";
  }

  // OAuth errors
  if (errorLower.includes("oauth") || errorLower.includes("provider")) {
    return "Authentication failed. Please try again or use email login.";
  }

  // Generic fallback
  return error;
};

const VCLogin: React.FC = () => {
  const navigate = useNavigate();
  const {
    signInWithGoogle,
    requestEmailLoginOtp,
    error,
    clearError,
    isAuthenticated,
    loading,
    onboardingLoading,
    isOnboarded,
    userRole,
    confirmationEmailSent,
  } = useAuth();

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [emailLoading, setEmailLoading] = useState<boolean>(false);
  const [googleLoading, setGoogleLoading] = useState<boolean>(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const displayedError = getUserFriendlyError(localError || error);

  // Apply dark theme
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.classList.add("login-dark");
      document.body.classList.add("login-dark");
      return () => {
        document.documentElement.classList.remove("login-dark");
        document.body.classList.remove("login-dark");
      };
    }
  }, []);

  // Handle redirect after successful authentication
  useEffect(() => {
    // Wait for auth and onboarding status to be determined
    if (loading || (isAuthenticated && onboardingLoading)) {
      return;
    }

    // Only redirect if authenticated and we're still on the login page
    if (isAuthenticated && userRole === "vc") {
      if (!isOnboarded) {
        // Not onboarded -> redirect to VC onboarding
        navigate("/onboarding/vc", { replace: true });
      } else {
        // Onboarded -> redirect to VC dashboard
        navigate("/vc-dashboard", { replace: true });
      }
    }
  }, [
    isAuthenticated,
    loading,
    onboardingLoading,
    isOnboarded,
    userRole,
    navigate,
  ]);

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setLocalError(null);
    clearError();
    try {
      await signInWithGoogle();
      // Redirect will be handled by useEffect when auth state changes
    } catch (err) {
      console.error("Google login error:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Google sign-in failed";
      setLocalError(errorMessage);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setEmailLoading(true);
    setLocalError(null);
    clearError();
    try {
      if (password && password.length >= 8) {
        // Password-based login
        await authService.login({ email, password });
        // Redirect will be handled by useEffect when auth state changes
      } else {
        // Email OTP login
        await requestEmailLoginOtp(email, { rememberMe: false });
        // OTP email sent - user will be notified via confirmationEmailSent
      }
    } catch (err) {
      console.error("Login error:", err);
      const errorMessage = err instanceof Error ? err.message : "Login failed";
      setLocalError(errorMessage);
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.glowDots} aria-hidden="true">
        <div
          className={`${styles.dot} ${styles.dotBlue}`}
          style={{ top: "8%", left: "12%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotPurple}`}
          style={{ top: "20%", right: "10%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotYellow}`}
          style={{ bottom: "18%", left: "22%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotRed}`}
          style={{ bottom: "10%", right: "18%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotTeal}`}
          style={{ top: "40%", left: "8%" }}
        />
        <div
          className={`${styles.dot} ${styles.dotPink}`}
          style={{ top: "65%", right: "8%" }}
        />
      </div>

      <div className={styles.container}>
        <div className={styles.logoSection}>
          <img
            src="/favicon.png"
            alt="SendUpdates.ai"
            className={styles.logoImage}
            style={{ width: "48px", height: "48px", borderRadius: "8px" }}
          />
          <h1 className={styles.logoText}>
            SendUpdates<span className={styles.logoHighlight}>.ai</span>
          </h1>
          <p className={styles.subtitle}>VC Portal Login</p>
        </div>

        <div className={styles.card}>
          <h2 className={styles.title}>Welcome Back</h2>
          <p className={styles.description}>
            Sign in to access your VC dashboard
          </p>

          {confirmationEmailSent && (
            <div
              className={styles.errorBanner}
              style={{
                backgroundColor: "rgba(34, 197, 94, 0.1)",
                borderColor: "rgba(34, 197, 94, 0.3)",
                color: "rgb(187, 247, 208)",
              }}
            >
              <span className={styles.errorIcon}>✓</span>
              <div>
                <strong>Check your email!</strong> We&apos;ve sent you a sign-in
                link. Click the link in the email to complete your login.
              </div>
            </div>
          )}

          {displayedError && !confirmationEmailSent && (
            <div className={styles.errorBanner}>
              <span className={styles.errorIcon}>⚠️</span>
              <div style={{ flex: 1 }}>{displayedError}</div>
              <button
                type="button"
                onClick={() => {
                  setLocalError(null);
                  clearError();
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "inherit",
                  cursor: "pointer",
                  padding: "4px",
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "8px",
                }}
                aria-label="Dismiss error"
              >
                <CloseIcon width={16} height={16} />
              </button>
            </div>
          )}

          {/* <button
            onClick={handleGoogleLogin}
            disabled={googleLoading || emailLoading}
            className={styles.socialButton}
          >
            {googleLoading ? (
              <InlineSpinner />
            ) : (
              <GoogleIcon className={styles.socialIcon} />
            )}
            <span>Continue with Google</span>
          </button> */}

          {/* <div className={styles.divider}>
            <span className={styles.dividerText}>or</span>
          </div> */}

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.inputGroup}>
              <div className={styles.inputIcon}>
                <UserIcon />
              </div>
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={emailLoading || googleLoading}
                className={styles.input}
              />
            </div>

            <div className={styles.inputGroup}>
              <div className={styles.inputIcon}>
                <LockIcon />
              </div>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password (optional)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={emailLoading || googleLoading}
                className={styles.input}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={styles.eyeButton}
                disabled={emailLoading || googleLoading}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>

            <p className={styles.hint}>
              Leave password blank to receive a sign-in link via email
            </p>

            <button
              type="submit"
              disabled={emailLoading || googleLoading}
              className={styles.submitButton}
            >
              {emailLoading ? <InlineSpinner /> : "Sign In"}
            </button>
          </form>

          <div className={styles.footer}>
            Don&apos;t have an account?{" "}
            <Link to="/signup" className={styles.link}>
              Sign Up
            </Link>
          </div>

          <div className={styles.terms}>
            By signing in, you agree to our{" "}
            <Link to="/terms" className={styles.link}>
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className={styles.link}>
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VCLogin;
